/**
 * Plot job assembly (docs/opencadstudio-parity/02-plot-pdf-ctb.md §3.4):
 * turns a page-setup + plot options + the resolved ledger content into a
 * finalized PlotPage the PDF exporter consumes — the counterpart of OCS's
 * layout_plot_page_for / extents_plot_job.
 */

import { useLayoutStore } from '../../state/useLayoutStore';
import { buildPlotContent, modelExtents } from './plotPageBuilder';
import { effectiveScaleMmPerUnit, normalizeWindow } from './pageSetup';
import { updateFields } from '../../core/commands/fields';
import type { PageSetup, PlotAreaKind, PlotOptions, PlotPage, PlotWindow } from './types';

export interface PlotJobRequest {
    plotType: PlotAreaKind;
    /** Layout id for layout plots; null = model space. */
    layoutId: string | null;
    window?: PlotWindow;
    setup: PageSetup;
    options: PlotOptions;
    /** Sheet title (stamp + progress messages). */
    title: string;
}

const M_PER_MM = 0.001;

/**
 * Builds the content and resolves scale/origin/offset for one sheet.
 * Layout plots pin 1:1 paper (the sheet IS paper space); model plots use
 * fit-to-paper (bounds inflated 5%, OCS MARGIN) or the num:den scale.
 */
export function buildPlotPage(req: PlotJobRequest): PlotPage {
    // FIELD plot trigger (Faz 8.8, FIELDEVAL bit 4): live values refresh
    // synchronously before the page content resolves (OCS stamp_plot_fields).
    updateFields('plot');
    const content = buildPlotContent({
        plotType: req.plotType,
        layoutId: req.layoutId,
        window: req.window,
        options: req.options,
    });

    const paperWidth = req.setup.paperWidth;
    const paperHeight = req.setup.paperHeight;
    const rotated = req.setup.rotation === 90 || req.setup.rotation === 270;
    // Content box (unrotated mm): the sheet dims swap for 90/270 so the
    // rotated content still fills the sheet exactly.
    const cw = rotated ? paperHeight : paperWidth;
    const ch = rotated ? paperWidth : paperHeight;

    if (req.plotType === 'layout') {
        const layout = useLayoutStore.getState().layouts.find((l) => l.id === req.layoutId);
        const canvasW = (layout?.width ?? paperWidth * M_PER_MM) * 1000;
        const canvasH = (layout?.height ?? paperHeight * M_PER_MM) * 1000;
        let offsetX = req.setup.offsetX;
        let offsetY = req.setup.offsetY;
        if (req.setup.centerPlot) {
            offsetX = (cw - canvasW) / 2;
            offsetY = (ch - canvasH) / 2;
        }
        return {
            title: req.title,
            content,
            paperWidth, paperHeight,
            rotation: req.setup.rotation,
            scale: 1000,
            originX: 0, originY: 0,
            offsetX, offsetY,
            options: req.options,
            plotStyleName: req.setup.plotStyleName,
            stampLabel: req.title,
        };
    }

    // Model-space plots: resolve the plot area first.
    let area: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
    if (req.plotType === 'window' && req.window) area = normalizeWindow(req.window);
    else area = modelExtents();

    const w = area ? Math.max(area.maxX - area.minX, 1e-9) : 1;
    const h = area ? Math.max(area.maxY - area.minY, 1e-9) : 1;
    const fitBounds = req.setup.fitToPaper ? { w, h } : null;
    const { scale } = effectiveScaleMmPerUnit(req.setup, fitBounds);

    // Fit mode and scale mode both place the plot area's lower-left corner
    // at the plot origin (AutoCAD behavior for window/extents/display);
    // centering then re-centers the content span on the sheet.
    const originX = area?.minX ?? 0;
    const originY = area?.minY ?? 0;
    let offsetX = req.setup.offsetX;
    let offsetY = req.setup.offsetY;
    if (req.setup.fitToPaper || req.setup.centerPlot) {
        const spanX = area ? (area.maxX - originX) * scale : 0;
        const spanY = area ? (area.maxY - originY) * scale : 0;
        offsetX = (cw - spanX) / 2;
        offsetY = (ch - spanY) / 2;
    }

    return {
        title: req.title,
        content,
        paperWidth, paperHeight,
        rotation: req.setup.rotation,
        scale,
        originX, originY,
        offsetX, offsetY,
        options: req.options,
        plotStyleName: req.setup.plotStyleName,
        stampLabel: req.title,
    };
}

/** Scale label for the stamp: 'Fit' or the num:den ratio (or 1:1 layout). */
export function stampScaleLabel(setup: PageSetup): string {
    if (setup.plotType === 'layout') return '1:1';
    if (setup.fitToPaper) return 'Fit';
    const n = setup.scaleNum > 0 ? setup.scaleNum : 1;
    const d = setup.scaleDen > 0 ? setup.scaleDen : 1;
    return `${n}:${d}`;
}
